import { useNavigate, useLocation } from 'react-router-dom';
import { Book, Search, BookOpen, BookMarked, BookText, LogIn, LogOut, Sparkles, HelpCircle, Settings, Crown } from 'lucide-react';
import { useAuthStore } from '../../services/auth';
import { useTutorialStore } from '../../stores/tutorialStore';
import { useState, useEffect } from 'react';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function Sidebar({ open, setOpen }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const setShowAuthModal = useAuthStore((state) => state.setShowAuthModal);
  const signOut = useAuthStore((state) => state.signOut);
  const setTutorialActive = useTutorialStore((state) => state.setIsActive);
  const resetTutorial = useTutorialStore((state) => state.resetTutorial);
  const isActive = useTutorialStore((state) => state.isActive);
  const currentTutorial = useTutorialStore((state) => state.currentTutorial);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAuthModal = () => {
    setShowAuthModal(true);
    setOpen(false); // Close sidebar when opening auth modal
  };

  const navigationItems = [
    { id: 'read', label: 'Bible Reader', icon: Book, path: '/' },
    { id: 'ai-insights', label: 'AI Insights', icon: Sparkles, path: '/ai-insights', requiresAuth: true },
    { id: 'mood', label: 'Mood Scriptures', icon: BookMarked, path: '/mood-scriptures', requiresAuth: true },
    { id: 'study', label: 'Bible Study', icon: BookText, path: '/study' },
    { id: 'notes', label: 'Study Notes', icon: BookOpen, requiresAuth: true, path: '/notes' },
    { id: 'search', label: 'Search', icon: Search, path: '/search', skipPulse: true },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-full h-full bg-white/5 backdrop-blur-lg border-r border-white/10 flex flex-col">
      {/* Main Navigation */}
      <nav className="p-4 space-y-2" data-tutorial="sidebar-navigation">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isDisabled = item.requiresAuth && !user;
          const isActive = isActivePath(item.path);

          return (
            <button
              key={item.id}
              onClick={() => {
                if (isDisabled) {
                  handleAuthModal();
                  return;
                }
                navigate(item.path);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isDisabled
                  ? 'text-white/40 hover:bg-white/5'
                  : isActive
                    ? 'text-white bg-white/15 shadow-inner border border-white/10'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
              } ${item.id === 'search' ? '' : isActive ? 'tutorial-pulse' : ''}`}
              data-tutorial={item.id}
            >
              <Icon size={20} className={isActive ? 'text-gold-400' : ''} />
              <span>{item.label}</span>
              {isDisabled && (
                <span className="ml-auto text-xs text-white/40">Sign in required</span>
              )}
            </button>
          );
        })}

        {/* Tutorial Button */}
        <button
          onClick={() => {
            resetTutorial();  // Reset to start
            setTutorialActive(true);  // Show tutorial
            // Keep sidebar open during tutorial
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-white/60 hover:text-white hover:bg-white/10"
          data-tutorial="tutorial-button"
        >
          <HelpCircle size={20} />
          <span>App Tutorial</span>
        </button>

        {/* Auth button as last item in navigation */}
        {user ? (
          <>
            <button
              onClick={() => navigate('/preferences')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname === '/preferences'
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span>Preferences</span>
            </button>
            <button
              onClick={() => {
                signOut();
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </>
        ) : (
          <button
            onClick={() => setShowAuthModal(true)}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <LogIn className="w-5 h-5" />
            <span>Sign In</span>
          </button>
        )}
      </nav>
    </aside>
  );
}
